import { readPsd, type Layer, type Psd, type Color } from "ag-psd";

import type {
  ParsedPsd,
  SlotRect,
  Template,
  TemplateLayer,
  TemplateMask,
} from "../types";
import { slugifyId, uniqueImageName } from "./slugify";
import { canvasToWebpBlobCapped, webpOrPng } from "./webp";

const DEFAULT_THUMB_MAX_SIZE = 96;

function colorToHex(color: Color | undefined): string {
  if (!color) return "#000000";
  const anyColor = color as Record<string, number>;
  const r = clamp(Math.round(anyColor.r ?? 0));
  const g = clamp(Math.round(anyColor.g ?? 0));
  const b = clamp(Math.round(anyColor.b ?? 0));
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

function clamp(v: number): number {
  return Math.max(0, Math.min(255, v));
}

function toHex(v: number): string {
  return v.toString(16).padStart(2, "0");
}

function round3(v: number): number {
  return Math.round(v * 1000) / 1000;
}

function isGroup(layer: Layer): boolean {
  return Array.isArray(layer.children);
}

function computeSlotRect(
  layer: Layer,
  templateWidth: number,
  templateHeight: number,
  mask: TemplateMask | undefined,
): SlotRect {
  const lx1 = Math.max(0, layer.left ?? 0);
  const ly1 = Math.max(0, layer.top ?? 0);
  const lx2 = Math.min(templateWidth, layer.right ?? lx1);
  const ly2 = Math.min(templateHeight, layer.bottom ?? ly1);

  if (!mask || mask.width === 0 || mask.height === 0) {
    return { x: lx1, y: ly1, width: Math.max(0, lx2 - lx1), height: Math.max(0, ly2 - ly1) };
  }

  if (mask.defaultColor === 0) {
    const x1 = Math.max(lx1, mask.left);
    const y1 = Math.max(ly1, mask.top);
    const x2 = Math.min(lx2, mask.right);
    const y2 = Math.min(ly2, mask.bottom);
    return { x: x1, y: y1, width: Math.max(0, x2 - x1), height: Math.max(0, y2 - y1) };
  }

  // defaultColor=255: visible = layer bbox MINUS mask bbox. Pick the largest rectangular slice.
  const intersectTop = Math.max(ly1, mask.top);
  const intersectBottom = Math.min(ly2, mask.bottom);
  const candidates: SlotRect[] = [
    { x: lx1, y: ly1, width: lx2 - lx1, height: Math.max(0, mask.top - ly1) },
    {
      x: lx1,
      y: Math.max(ly1, mask.bottom),
      width: lx2 - lx1,
      height: Math.max(0, ly2 - Math.max(ly1, mask.bottom)),
    },
    {
      x: lx1,
      y: intersectTop,
      width: Math.max(0, mask.left - lx1),
      height: Math.max(0, intersectBottom - intersectTop),
    },
    {
      x: Math.max(lx1, mask.right),
      y: intersectTop,
      width: Math.max(0, lx2 - Math.max(lx1, mask.right)),
      height: Math.max(0, intersectBottom - intersectTop),
    },
  ];
  candidates.sort((a, b) => b.width * b.height - a.width * a.height);
  const best = candidates[0];
  if (best && best.width > 0 && best.height > 0) return best;
  return { x: lx1, y: ly1, width: Math.max(0, lx2 - lx1), height: Math.max(0, ly2 - ly1) };
}

function buildMask(layer: Layer): TemplateMask | undefined {
  const mask = layer.mask;
  if (!mask) return undefined;
  const top = mask.top ?? 0;
  const left = mask.left ?? 0;
  const bottom = mask.bottom ?? 0;
  const right = mask.right ?? 0;
  const width = Math.max(0, right - left);
  const height = Math.max(0, bottom - top);
  const positionRelative = mask.positionRelativeToLayer !== false;
  const disabled = mask.disabled === true;
  const flags = (positionRelative ? 1 : 0) | (disabled ? 2 : 0);
  return {
    top,
    left,
    bottom,
    right,
    width,
    height,
    defaultColor: mask.defaultColor ?? 0,
    flags,
  };
}

function makeThumbnail(canvas: HTMLCanvasElement, max = DEFAULT_THUMB_MAX_SIZE): string {
  const ratio = canvas.width / canvas.height || 1;
  let w = max;
  let h = max;
  if (ratio >= 1) h = Math.max(1, Math.round(max / ratio));
  else w = Math.max(1, Math.round(max * ratio));
  const thumb = document.createElement("canvas");
  thumb.width = w;
  thumb.height = h;
  const ctx = thumb.getContext("2d");
  if (ctx) {
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = "high";
    ctx.drawImage(canvas, 0, 0, w, h);
  }
  return thumb.toDataURL("image/png");
}

interface FlattenContext {
  layers: TemplateLayer[];
  images: Map<string, Blob>;
  canvases: Map<string, HTMLCanvasElement>;
  thumbnails: Map<string, string>;
  usedFileNames: Set<string>;
  imageTasks: Promise<void>[];
  zCounter: { value: number };
  templateWidth: number;
  templateHeight: number;
  /** Rect of the latest non-clipped base layer (slot/image/fill); clipped adjustments clip to it. */
  clipBaseRect?: SlotRect;
}

/** Builds clip fields for an adjustment layer clipped to the current base (the layer below). */
function clipFields(
  clipped: boolean,
  base: SlotRect | undefined,
): { clipX: number; clipY: number; clipWidth: number; clipHeight: number } | Record<string, never> {
  if (clipped && base && base.width > 0 && base.height > 0) {
    return { clipX: base.x, clipY: base.y, clipWidth: base.width, clipHeight: base.height };
  }
  return {};
}

function detectVectorFillColor(layer: Layer): string | undefined {
  const vf = layer.vectorFill;
  if (vf && vf.type === "color") {
    return colorToHex(vf.color);
  }
  return undefined;
}

function getPhotoFilter(layer: Layer): { color: string; density: number; preserveLuminosity: boolean } | undefined {
  const adj = layer.adjustment;
  if (adj && adj.type === "photo filter") {
    return {
      color: colorToHex(adj.color),
      density: adj.density ?? 0.25,
      preserveLuminosity: adj.preserveLuminosity ?? true,
    };
  }
  return undefined;
}

function getBlackAndWhite(layer: Layer): {
  weights: { reds: number; yellows: number; greens: number; cyans: number; blues: number; magentas: number };
  useTint?: boolean;
  tintColor?: string;
} | undefined {
  const adj = layer.adjustment;
  if (adj && adj.type === "black & white") {
    return {
      weights: {
        reds: adj.reds ?? 40,
        yellows: adj.yellows ?? 60,
        greens: adj.greens ?? 40,
        cyans: adj.cyans ?? 60,
        blues: adj.blues ?? 20,
        magentas: adj.magentas ?? 80,
      },
      useTint: adj.useTint,
      tintColor: adj.useTint ? colorToHex(adj.tintColor) : undefined,
    };
  }
  return undefined;
}

function getBrightnessContrast(
  layer: Layer,
): { brightness: number; contrast: number; useLegacy: boolean } | undefined {
  const adj = layer.adjustment;
  if (adj && adj.type === "brightness/contrast") {
    return {
      brightness: adj.brightness ?? 0,
      contrast: adj.contrast ?? 0,
      useLegacy: adj.useLegacy ?? false,
    };
  }
  return undefined;
}

function getHueSaturation(
  layer: Layer,
): { hue: number; saturation: number; lightness: number } | undefined {
  const adj = layer.adjustment;
  if (adj && adj.type === "hue/saturation") {
    const m = adj.master;
    return {
      hue: m?.hue ?? 0,
      saturation: m?.saturation ?? 0,
      lightness: m?.lightness ?? 0,
    };
  }
  return undefined;
}

function getVibrance(layer: Layer): { vibrance: number; saturation: number } | undefined {
  const adj = layer.adjustment;
  if (adj && adj.type === "vibrance") {
    return {
      vibrance: adj.vibrance ?? 0,
      saturation: adj.saturation ?? 0,
    };
  }
  return undefined;
}

function colorBalanceTriplet(
  v: { cyanRed?: number; magentaGreen?: number; yellowBlue?: number } | undefined,
): { cyanRed: number; magentaGreen: number; yellowBlue: number } {
  return {
    cyanRed: v?.cyanRed ?? 0,
    magentaGreen: v?.magentaGreen ?? 0,
    yellowBlue: v?.yellowBlue ?? 0,
  };
}

function getColorBalance(layer: Layer):
  | {
      shadows: { cyanRed: number; magentaGreen: number; yellowBlue: number };
      midtones: { cyanRed: number; magentaGreen: number; yellowBlue: number };
      highlights: { cyanRed: number; magentaGreen: number; yellowBlue: number };
      preserveLuminosity: boolean;
    }
  | undefined {
  const adj = layer.adjustment;
  if (adj && adj.type === "color balance") {
    return {
      shadows: colorBalanceTriplet(adj.shadows),
      midtones: colorBalanceTriplet(adj.midtones),
      highlights: colorBalanceTriplet(adj.highlights),
      preserveLuminosity: adj.preserveLuminosity ?? true,
    };
  }
  return undefined;
}

function cmyk(
  v: { c?: number; m?: number; y?: number; k?: number } | undefined,
): { c: number; m: number; y: number; k: number } {
  return { c: v?.c ?? 0, m: v?.m ?? 0, y: v?.y ?? 0, k: v?.k ?? 0 };
}

type CMYK = { c: number; m: number; y: number; k: number };

function getSelectiveColor(layer: Layer):
  | {
      mode: "relative" | "absolute";
      reds: CMYK;
      yellows: CMYK;
      greens: CMYK;
      cyans: CMYK;
      blues: CMYK;
      magentas: CMYK;
      whites: CMYK;
      neutrals: CMYK;
      blacks: CMYK;
    }
  | undefined {
  const adj = layer.adjustment;
  if (adj && adj.type === "selective color") {
    return {
      mode: adj.mode === "absolute" ? "absolute" : "relative",
      reds: cmyk(adj.reds),
      yellows: cmyk(adj.yellows),
      greens: cmyk(adj.greens),
      cyans: cmyk(adj.cyans),
      blues: cmyk(adj.blues),
      magentas: cmyk(adj.magentas),
      whites: cmyk(adj.whites),
      neutrals: cmyk(adj.neutrals),
      blacks: cmyk(adj.blacks),
    };
  }
  return undefined;
}

function getGradientMap(
  layer: Layer,
): { stops: Array<{ location: number; color: string }>; reverse: boolean } | undefined {
  const adj = layer.adjustment;
  if (adj && adj.type === "gradient map") {
    const stops = (adj.colorStops ?? [])
      .map((s) => ({ location: s.location ?? 0, color: colorToHex(s.color) }))
      .sort((a, b) => a.location - b.location);
    if (stops.length === 0) return undefined;
    return { stops, reverse: adj.reverse ?? false };
  }
  return undefined;
}

function processLayer(layer: Layer, ctx: FlattenContext, group: string | undefined): void {
  if (layer.hidden) return;
  if (isGroup(layer)) {
    const groupName = layer.name ?? group;
    // Clipping does not cross group boundaries in Photoshop.
    const savedBase = ctx.clipBaseRect;
    ctx.clipBaseRect = undefined;
    for (const child of layer.children ?? []) {
      processLayer(child, ctx, groupName);
    }
    ctx.clipBaseRect = savedBase;
    return;
  }

  const z = ctx.zCounter.value++;
  const left = layer.left ?? 0;
  const top = layer.top ?? 0;
  const right = layer.right ?? left;
  const bottom = layer.bottom ?? top;
  const width = Math.max(0, right - left);
  const height = Math.max(0, bottom - top);

  const rawOpacity = layer.opacity;
  const opacity = rawOpacity != null && rawOpacity < 1 ? round3(rawOpacity) : undefined;
  const blendMode = layer.blendMode && layer.blendMode !== "normal" ? layer.blendMode : undefined;
  const clipped = layer.clipping === true ? true : undefined;
  const mask = buildMask(layer);
  const slotRect = computeSlotRect(layer, ctx.templateWidth, ctx.templateHeight, mask);

  const base = {
    name: layer.name ?? "Layer",
    ...(group ? { group } : {}),
    x: left,
    y: top,
    width,
    height,
    z,
    ...(opacity != null ? { opacity } : {}),
    ...(blendMode ? { blendMode } : {}),
    ...(clipped ? { clipped } : {}),
    ...(mask ? { mask } : {}),
    slotRect,
  };

  // Region a clipped adjustment applies to (the base layer below it, usually the slot).
  const clip = clipFields(clipped === true, ctx.clipBaseRect);

  const photoFilter = getPhotoFilter(layer);
  if (photoFilter) {
    ctx.layers.push({
      ...base,
      ...clip,
      type: "photoFilter",
      color: photoFilter.color,
      density: photoFilter.density,
      preserveLuminosity: photoFilter.preserveLuminosity,
    });
    return;
  }

  const blackAndWhite = getBlackAndWhite(layer);
  if (blackAndWhite) {
    ctx.layers.push({
      ...base,
      ...clip,
      type: "blackAndWhite",
      weights: blackAndWhite.weights,
      ...(blackAndWhite.useTint ? { useTint: true } : {}),
      ...(blackAndWhite.tintColor ? { tintColor: blackAndWhite.tintColor } : {}),
    });
    return;
  }

  const brightnessContrast = getBrightnessContrast(layer);
  if (brightnessContrast) {
    ctx.layers.push({
      ...base,
      ...clip,
      type: "brightnessContrast",
      brightness: brightnessContrast.brightness,
      contrast: brightnessContrast.contrast,
      ...(brightnessContrast.useLegacy ? { useLegacy: true } : {}),
    });
    return;
  }

  const hueSaturation = getHueSaturation(layer);
  if (hueSaturation) {
    ctx.layers.push({
      ...base,
      ...clip,
      type: "hueSaturation",
      hue: hueSaturation.hue,
      saturation: hueSaturation.saturation,
      lightness: hueSaturation.lightness,
    });
    return;
  }

  const vibrance = getVibrance(layer);
  if (vibrance) {
    ctx.layers.push({
      ...base,
      ...clip,
      type: "vibrance",
      vibrance: vibrance.vibrance,
      saturation: vibrance.saturation,
    });
    return;
  }

  const colorBalance = getColorBalance(layer);
  if (colorBalance) {
    ctx.layers.push({
      ...base,
      ...clip,
      type: "colorBalance",
      shadows: colorBalance.shadows,
      midtones: colorBalance.midtones,
      highlights: colorBalance.highlights,
      preserveLuminosity: colorBalance.preserveLuminosity,
    });
    return;
  }

  const selectiveColor = getSelectiveColor(layer);
  if (selectiveColor) {
    ctx.layers.push({
      ...base,
      ...clip,
      type: "selectiveColor",
      mode: selectiveColor.mode,
      reds: selectiveColor.reds,
      yellows: selectiveColor.yellows,
      greens: selectiveColor.greens,
      cyans: selectiveColor.cyans,
      blues: selectiveColor.blues,
      magentas: selectiveColor.magentas,
      whites: selectiveColor.whites,
      neutrals: selectiveColor.neutrals,
      blacks: selectiveColor.blacks,
    });
    return;
  }

  const gradientMap = getGradientMap(layer);
  if (gradientMap) {
    ctx.layers.push({
      ...base,
      ...clip,
      type: "gradientMap",
      stops: gradientMap.stops,
      ...(gradientMap.reverse ? { reverse: true } : {}),
    });
    return;
  }

  const fillColor = detectVectorFillColor(layer);
  if (fillColor) {
    if (!clipped) ctx.clipBaseRect = slotRect;
    ctx.layers.push({
      ...base,
      type: "colorFill",
      color: fillColor,
    });
    return;
  }

  if (layer.canvas) {
    const fileName = uniqueImageName(base.name, ctx.usedFileNames, webpOrPng().ext);
    const canvas = layer.canvas;
    ctx.canvases.set(fileName, canvas);
    ctx.thumbnails.set(fileName, makeThumbnail(canvas));
    ctx.imageTasks.push(
      canvasToWebpBlobCapped(canvas).then((blob) => {
        ctx.images.set(fileName, blob);
      }),
    );
    // Auto-detect: layer named slot_xxx becomes a slot
    if (/^slot[_-]?\w+/i.test(base.name) && slotRect.width > 0 && slotRect.height > 0) {
      if (!clipped) ctx.clipBaseRect = slotRect;
      ctx.layers.push({
        name: base.name,
        ...(group ? { group } : {}),
        x: slotRect.x,
        y: slotRect.y,
        width: slotRect.width,
        height: slotRect.height,
        z: base.z,
        type: "slot",
        id: base.name,
      });
      // still keep canvas thumbnail association so UI can preview the original placeholder
      return;
    }
    if (!clipped) ctx.clipBaseRect = slotRect;
    ctx.layers.push({
      ...base,
      type: "image",
      imageUrl: fileName,
    });
    return;
  }

  ctx.layers.push({ ...base, type: "unknown" });
}

export async function parsePsd(file: File): Promise<ParsedPsd> {
  const buffer = await file.arrayBuffer();
  const psd: Psd = readPsd(buffer, { skipThumbnail: true });

  const name = file.name.replace(/\.psd$/i, "").trim() || "Template";
  const id = slugifyId(name);

  const ctx: FlattenContext = {
    layers: [],
    images: new Map(),
    canvases: new Map(),
    thumbnails: new Map(),
    usedFileNames: new Set(),
    imageTasks: [],
    zCounter: { value: 0 },
    templateWidth: psd.width,
    templateHeight: psd.height,
  };

  for (const child of psd.children ?? []) {
    processLayer(child, ctx, undefined);
  }

  await Promise.all(ctx.imageTasks);

  const template: Template = {
    id,
    name,
    width: psd.width,
    height: psd.height,
    layers: ctx.layers,
  };

  return {
    template,
    images: ctx.images,
    canvases: ctx.canvases,
    thumbnails: ctx.thumbnails,
  };
}
